import {
  ContextMenu,
  ContextMenuProps,
  List,
  MenuOption,
} from '@infotech-soe/erp-ui-kit';
import classNames from 'classnames';
import noop from 'lodash/noop';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { ApplicationMenuContainerProps } from './ApplicationMenuContainer';
import MenuBackdrop from './MenuBackdrop';
import MenuContext from './MenuContext';
import MenuItems from './MenuItems';
import MenuToggleButton from './MenuToggleButton';
import { itemMessages } from './messages';
import { useMenuStyles } from './styles';

export const enum ActionNames {
  ADD_TO_PANELLINKS = 'toPanelLinks',
  OPEN = 'open',
  ACTIVATE = 'activate',
  OPEN_DEFAULT = 'openDefault',
}

export interface IContextMenuOptions {
  isCategoryOpen: boolean;
  reference: ContextMenuProps['position'];
  onContextMenuClick: (name: ActionNames) => void;
}

const defaultContextMenuOptions: IContextMenuOptions = {
  isCategoryOpen: false,
  reference: null,
  onContextMenuClick: noop,
};

const ApplicationMenu = (
  props: ApplicationMenuContainerProps
): React.JSX.Element => {
  const { collapsed, items } = props;
  const { formatMessage } = useIntl();
  const classes = useMenuStyles();

  const [{ isCategoryOpen, reference, onContextMenuClick }, setOptions] =
    useState<IContextMenuOptions>(defaultContextMenuOptions);

  const closeContextMenu = useCallback(() => {
    setOptions(defaultContextMenuOptions);
  }, []);

  const options: Array<MenuOption<ActionNames>> = [
    {
      type: 'listitem',
      value: ActionNames.ADD_TO_PANELLINKS,
      label: formatMessage(itemMessages[ActionNames.ADD_TO_PANELLINKS]),
      icon: 'sort-arrow',
    },
    {
      type: 'listitem',
      value: ActionNames.OPEN,
      label: formatMessage(itemMessages[ActionNames.OPEN]),
      icon: 'create',
    },
    {
      type: 'listitem',
      value: ActionNames.ACTIVATE,
      label: formatMessage(itemMessages[ActionNames.ACTIVATE]),
      icon: 'ui-tab',
      disabled: !isCategoryOpen,
    },
    {
      type: 'listitem',
      value: ActionNames.OPEN_DEFAULT,
      label: formatMessage(itemMessages[ActionNames.OPEN_DEFAULT]),
      icon: 'reforming',
    },
  ];

  const contextValue = useMemo(
    () => ({ ...props, setOptions }),
    [props, setOptions]
  );

  return (
    <MenuContext.Provider value={contextValue}>
      <MenuBackdrop />
      <aside
        className={classNames(classes.sidebar, {
          [classes.collapsed]: collapsed,
        })}
      >
        {!!items.length && (
          <List
            autoActivate
            className={classes.list}
            focusOnActivation
            withHotkeys
          >
            <MenuToggleButton />
            <MenuItems show={true} items={items} />
          </List>
        )}
      </aside>
      {reference && (
        <ContextMenu
          open={!!reference}
          options={options}
          position={reference}
          onClick={onContextMenuClick}
          onOpen={closeContextMenu}
        />
      )}
    </MenuContext.Provider>
  );
};

export default memo(ApplicationMenu);
